import cx from 'classnames';
import { Copy, Link as LinkIcon, Stop, TrashClear } from 'education-icons';
import { showIntlMessageNotification, SimpleCallback, Text, ThreeDots, Menu, PopupType } from 'platform-components';
import { GRAVITIES, NOTIFICATION_THEMES, POPUP_DIRECTIONS } from 'platform-components/constants';
import { BUTTON_SIZE } from 'platform-components/src/components/button/types';
import { getMe } from 'platform-components/src/components/user/selectors';
import { copyTextToClipboard } from 'platform-components/utils';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { useMedia } from 'react-use';
import ym from 'react-yandex-metrika';

import { COMMON_NOTIFICATION_DELAY, LIBRARY_CONTENT_TYPE } from 'common.components/constants';
import { LessonPermissions } from 'utils/lesson';

import { SCREEN_QUERY } from '../lesson-settings/constants';
import { b } from './cn';

import { LessonPutOnBoard } from './__put-on-board/lesson-three-dots__put-on-board';
import './lesson-three-dots.scss';

interface Props {
    childLink: string;
    disabled: boolean;
    onClick?: SimpleCallback;
    onLessonDelete: SimpleCallback;
    onLessonCopy: SimpleCallback;
    onLessonClose: SimpleCallback;
    lessonPermissions: LessonPermissions;
    clessonId: number;
    courseId: number | null;
    mix?: string;
}

// eslint-disable-next-line complexity
export const LessonThreeDots = ({
    childLink,
    disabled,
    onClick,
    onLessonDelete,
    onLessonCopy,
    onLessonClose,
    lessonPermissions: {
        closeControlAvailable,
        copyOwnLessonControlAvailable,
        deleteControlAvailable,
        isPutOnBoardAvailable,
        isCopyLessonLinkAvailable,
    },
    clessonId,
    courseId,
    mix,
}: Props) => {
    const { subject } = useParams<{ subject: string }>();
    const isMobile = useMedia(SCREEN_QUERY.SMALL);
    const [popup, setPopup] = useState<PopupType | null>(null);

    const me = useSelector(getMe);
    const dispatch = useDispatch();

    const handlePutOnBoard = () => {
        ym('reachGoal', 'teacher_lesson_creation_preview', {
            subject,
            contentType: LIBRARY_CONTENT_TYPE.COURSE_LESSON,
            type: 'in-lesson',
            clessonId: clessonId,
        });
    };

    const handleCopy = (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();

        ym('reachGoal', 'teacher_my_lessons_copy', { clessonId });

        if (onLessonCopy) {
            onLessonCopy();
        }

        popup?.hide();
    };

    const handleCopyLessonLink = (e: React.MouseEvent<HTMLElement>, shouldShowToast?: boolean) => {
        e.preventDefault();
        e.stopPropagation();

        copyTextToClipboard(childLink);

        ym('reachGoal', 'teacher_copy_lesson_link', {
            user_id: me?.id,
            clesson_id: clessonId,
            from: 'my_lessons',
        });

        if (shouldShowToast) {
            dispatch(
                showIntlMessageNotification({
                    closeDelay: COMMON_NOTIFICATION_DELAY,
                    gravity: GRAVITIES.BOTTOM,
                    messageId: 'toast.lesson.linkCopied',
                    theme: NOTIFICATION_THEMES.TOAST,
                })
            );
        }

        popup?.hide();
    };

    const handleClose = (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();

        if (onLessonClose) {
            onLessonClose();
        }

        popup?.hide();
    };

    const handleDelete = (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();

        if (onLessonDelete) {
            onLessonDelete();
        }

        popup?.hide();
    };

    const items: Array<React.ReactNode> = [];

    if (isPutOnBoardAvailable && courseId) {
        items.push(
            <LessonPutOnBoard
                clessonId={clessonId}
                courseId={courseId}
                key="lesson-put-on-board"
                onClick={handlePutOnBoard}
            />
        );
    }

    if (copyOwnLessonControlAvailable) {
        items.push(
            <Menu.Item key="lesson-copy" mix={cx(b('item'), 'qa_lesson-settings-copy-lesson')} onClick={handleCopy}>
                <Copy className={b('control-icon')} />
                <Text id="lesson.settings.copy" weight={Text.WEIGHT.S} />
            </Menu.Item>
        );
    }

    if (isCopyLessonLinkAvailable && isMobile) {
        items.push(
            <Menu.Item
                key="lesson-link-copy"
                mix={cx(b('item'), 'qa_lesson-settings-copy-lesson-link')}
                onClick={(e: React.MouseEvent<HTMLLIElement>) => handleCopyLessonLink(e, isMobile)}
            >
                <LinkIcon className={b('control-icon')} />
                <Text id="lesson.settings.copyLessonLink" weight={Text.WEIGHT.S} />
            </Menu.Item>
        );
    }

    if (closeControlAvailable) {
        if (items.length) {
            items.push(<Menu.Divider key="menu-divider" />);
        }
        items.push(
            <Menu.Item key="lesson-close" mix={b('item')} onClick={handleClose}>
                <Stop className={b('control-icon')} />
                <Text id="lesson.settings.close" weight={Text.WEIGHT.S} />
            </Menu.Item>
        );
    }

    if (deleteControlAvailable) {
        if (items.length) {
            items.push(<Menu.Divider key="menu-divider" />);
        }
        items.push(
            <Menu.Item key="lesson-delete" mix={cx(b('item'), 'qa_lesson-settings-delete')} onClick={handleDelete}>
                <TrashClear className={b('control-icon')} />
                <Text id="lesson.settings.delete" weight={Text.WEIGHT.S} />
            </Menu.Item>
        );
    }

    if (!items.length) {
        return null;
    }

    return (
        <ThreeDots
            controlMix={cx(b('dots-control'), mix)}
            controlSize={BUTTON_SIZE.S}
            direction={POPUP_DIRECTIONS.LEFT}
            disabled={disabled}
            onClick={onClick}
            popupProps={{ ref: (ref: PopupType) => setPopup(ref), mix: b('popup') }}
        >
            <Menu theme={Menu.THEME.ROUNDED}>{items}</Menu>
        </ThreeDots>
    );
};
